/**
 * O portal é o vínculo. Este é o mapa papel → portal, e o único lugar que
 * sabe quais portais já existem no app novo (A005, A007).
 *
 * Domínio sabe O QUE um vínculo pode; o portal sabe COMO mostrar.
 */
import type { BrandRole } from '@eva/schemas';
import { domainConfig } from '../config';

export type PortalId = 'mantenedor' | 'pro' | 'marca' | 'empresa' | 'eu';

export interface PortalInfo {
  id: PortalId;
  /** Prefixo de rota no app. */
  path: string;
  /** Já existe no app unificado? Se não, a casca aponta para o legado. */
  available: boolean;
  /** Para onde ir enquanto não existe. */
  legacyUrl: string | null;
  label: string;
}

export const ROLE_LABELS: Record<BrandRole, string> = {
  MAINTAINER: 'Mantenedora',
  BRAND_ADMIN: 'Administração da marca',
  BRAND_CRM: 'Relacionamento (CRM)',
  BRAND_CMS: 'Conteúdo',
  PROVIDER: 'Profissional de saúde',
  COMPANY_MANAGER: 'Gestão de empresa',
  PATIENT: 'Usuária(o)',
  GUEST: 'Convidada',
};

const ADMIN_ROLES: BrandRole[] = ['BRAND_ADMIN', 'BRAND_CRM', 'BRAND_CMS'];

/**
 * O mantenedor governa o ecossistema (marcas, funcionalidades, equipe
 * inicial): portal próprio, `/mantenedor`. A administração de UMA marca é o
 * `/marca`, e ele NÃO alcança esse — ver `podeEntrarNoPortal`.
 */
export function portalIdFor(role: BrandRole): PortalId {
  if (role === 'MAINTAINER') return 'mantenedor';
  if (role === 'PROVIDER') return 'pro';
  if (role === 'COMPANY_MANAGER') return 'empresa';
  if (ADMIN_ROLES.includes(role)) return 'marca';
  return 'eu';
}

/**
 * Cada papel entra no portal do próprio vínculo. Inclusive o mantenedor.
 *
 * Ele já alcançou o painel de qualquer marca — a exceção existia porque
 * governar o ecossistema e administrar a marca Eva eram a mesma coisa. Não
 * são: governança é `/mantenedor`, operação é da equipe da marca. Quem precisa
 * olhar o dado de uma marca é nomeado nela, com uma conta de suporte
 * separada da de mantenedor (A024).
 */
export function podeEntrarNoPortal(role: BrandRole, portal: PortalId): boolean {
  return portalIdFor(role) === portal;
}

/**
 * Quais portais o app já serve. Atualizado a cada fase:
 *  - fase 1: `pro`
 *  - fase 2: `empresa`, e `marca` para concessões/empresas/parceiros
 *  - fase 3: `marca` completo
 *  - fase 4: `eu`
 */
const DISPONIVEIS: Record<PortalId, boolean> = {
  mantenedor: true,
  pro: true,
  marca: true,
  empresa: true,
  eu: true,
};

export function portalFor(role: BrandRole): PortalInfo {
  const id = portalIdFor(role);
  const { legacy } = domainConfig();
  const legacyUrl = id === 'eu' ? legacy.patientUrl : legacy.adminUrl;
  return {
    id,
    path: `/${id}`,
    available: DISPONIVEIS[id],
    legacyUrl,
    label: ROLE_LABELS[role],
  };
}

export function isPortalAvailable(id: PortalId): boolean {
  return DISPONIVEIS[id];
}
